import { getMetadataOptions } from "@/api/nfts/getMetadata";
import type { NFT } from "@/api/nfts/getNFTs";
import { OptimizedImage } from "@/shared/OptimizedImage";
import { Badge, Box, Group, Stack, Text } from "@mantine/core";
import { useSuspenseQuery } from "@tanstack/react-query";

export function BaseNFTCard({
	collectionAddress,
	nft,
	style,
	imageHeight,
	children,
}: {
	collectionAddress: string;
	nft: NFT;
	imageHeight?: number;
	style?: React.CSSProperties;
	children?: React.ReactNode;
}) {
	const nftMetadata = useSuspenseQuery(
		getMetadataOptions(collectionAddress, nft.tokenId),
	);

	return (
		<Stack
			gap={0}
			style={{
				height: "100%",
				borderRadius: "8px",
				backgroundColor: "white",
				boxShadow:
					"rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
				...style,
				overflow: "hidden",
			}}
		>
			<Box style={{ flex: "1 1 0", position: "relative" }}>
				<OptimizedImage
					src={nftMetadata.data?.image}
					alt={nftMetadata.data?.name || `NFT #${nft.tokenId}`}
					style={{
						minHeight: 200,
						width: "100%",
						height: imageHeight ?? "100%",
						objectFit: "cover",
					}}
				/>
			</Box>

			<Box p="md" style={{ flexShrink: 0, minHeight: 90 }}>
				<Text
					fw={500}
					style={{
						viewTransitionName: `nft-name-${nft.tokenId}`,
					}}
					mb="xs"
				>
					{nftMetadata.data?.name || `NFT #${nft.tokenId}`}
				</Text>

				<Group gap="xs">
					<Badge color="blue" variant="light">
						ID: {nft.tokenId}
					</Badge>
					{nft?.balance && (
						<Badge color="green" variant="light">
							Balance: {nft?.balance}
						</Badge>
					)}
				</Group>
				{children}
			</Box>
		</Stack>
	);
}
